/** Styled components for ScreenClient and ScreenSelector (split out of ScreenClient.jsx, #1166, and moved from styled-components to @emotion/styled on the way: plain CSS and $-props only, which both libraries treat alike). */
import styled from '@emotion/styled';

export const FadeLayer = styled.div`
  position: absolute;
  inset: 0;

  @keyframes layerFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes layerFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  ${props => props.$out
    ? 'animation: layerFadeOut 0.4s ease forwards; z-index: 1;'
    : 'animation: layerFadeIn 0.5s ease 0.3s backwards; z-index: 2;'}
`;

/* Room-wide blink: every wall fades to black at the same server-clock
   instant, swaps content under black, and fades back in together. */
export const SyncFadeOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: #000;
  z-index: 40;
  pointer-events: none;
  opacity: ${p => (p.$on ? 1 : 0)};
  transition: opacity 0.55s ease;
`;

export const Container = styled.div`
  width: 100%;
  height: var(--kuutio-vh, 100vh);
  max-height: var(--kuutio-vh, 100vh);
  background: #0a0a0a;
  overflow: hidden;
  position: relative;
`;

export const SelectorContainer = styled.div`
  height: var(--kuutio-vh, 100vh);
  max-height: var(--kuutio-vh, 100vh);
  background: #0a0a0a;
  padding: clamp(16px, 2.5vh, 40px);
  padding-bottom: clamp(24px, 4vh, 80px);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  box-sizing: border-box;
`;

export const SelectorTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

export const SelectorSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
`;

export const DropdownContainer = styled.div`
  position: relative;
  min-width: 320px;
  margin-bottom: 40px;
`;

export const DropdownButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(145deg, #1a1a1a, #222);
  border: 1px solid #333;
  border-radius: 12px;
  color: ${props => props.$hasValue ? '#fff' : '#666'};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0099ff;
    background: linear-gradient(145deg, #1e1e1e, #262626);
  }

  &:focus {
    outline: none;
    border-color: #0099ff;
    box-shadow: 0 0 0 3px rgba(0, 153, 255, 0.2);
  }
`;

export const DropdownArrow = styled.span`
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  z-index: 100;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: slideDown 0.15s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const DropdownItem = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: rgba(0, 153, 255, 0.15);
  }

  ${props => props.$selected && `
    background: rgba(0, 153, 255, 0.2);
    color: #0099ff;
  `}
`;

export const InstanceIcon = styled.span`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #0099ff, #9933ff);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

export const ScreenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 900px;
`;

export const WallSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const WallLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
`;

export const ScreenPair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ScreenButton = styled.button`
  width: 160px;
  height: 100px;
  background: ${props => props.$taken ? '#1a1a1a' : '#111'};
  border: 2px solid ${props => props.$taken ? '#ff4444' : '#333'};
  border-radius: 12px;
  color: #fff;
  font-family: 'JetBrains Mono', monospace;
  font-size: 28px;
  font-weight: 700;
  cursor: ${props => props.$taken ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$taken ? 0.6 : 1};
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    ${props => !props.$taken && `
      border-color: #00ff88;
      background: rgba(0, 255, 136, 0.1);
    `}
  }
`;

export const TakenBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background: #ff4444;
  border-radius: 50%;
`;

export const AvailableBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 12px;
  height: 12px;
  background: #00ff88;
  border-radius: 50%;
  opacity: 0.5;
`;

export const TransitionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 9999;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'all' : 'none'};
  transition: opacity 0.4s ease-in-out;
`;

export const FadeInContainer = styled.div`
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const WaitingScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #fff;
`;

export const ScreenCode = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 120px;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
`;

export const StatusText = styled.div`
  font-size: 18px;
  color: #666;
`;

export const ConnectionIndicator = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  font-size: 12px;
  color: ${props => props.$connected ? '#00ff88' : '#ff4444'};
  z-index: 100;
`;

export const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
`;

export const DisconnectedOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  color: white;
  text-align: center;
  gap: 20px;
`;

export const DisconnectedIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #ff4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
`;

export const DisconnectedTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: #ff4444;
`;

export const DisconnectedMessage = styled.p`
  font-size: 16px;
  color: #888;
  margin: 0;
`;

export const ReconnectButton = styled.button`
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 600;
  background: #0099ff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #0077cc;
    transform: scale(1.05);
  }

  &:disabled {
    background: #444;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ReconnectTimer = styled.div`
  font-size: 14px;
  color: #666;
`;

export const ErrorScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ff4444;
  text-align: center;
  padding: 40px;
`;

export const LoadingFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 18px;
`;

export const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 32px;
  margin-bottom: 16px;
  width: 100%;
  max-width: 900px;
`;

export const IPadGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 900px;
`;

export const IPadButton = styled.button`
  width: 80px;
  height: 80px;
  background: ${props => props.$taken ? '#1a1a1a' : '#111'};
  border: 2px solid ${props => props.$taken ? '#ff4444' : '#333'};
  border-radius: 12px;
  color: #fff;
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 700;
  cursor: ${props => props.$taken ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$taken ? 0.6 : 1};
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    ${props => !props.$taken && `
      border-color: #00ff88;
      background: rgba(0, 255, 136, 0.1);
    `}
  }
`;
